import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Database } from '@/integrations/supabase/types'
import { smartleadAPI } from '@/lib/smartlead-api'
import { DEMO_CLIENTS, getMailboxCountByDomain, DEMO_DOMAINS } from '@/data/demoData'

export type Client = Database['public']['Tables']['clients']['Row']

export interface ClientBasic {
  id: string
  name: string
  status: string
  client_id?: number
}

export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      // Always fetch from Supabase - clients data should be independent of Smartlead API
      // Only use demo data if Supabase query fails AND we're in demo mode
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, name, status, created_at, updated_at, email, phone, industry, client_id, company, main_contact_email, main_account_manager, domain, website, onboarding_notes, package_signed, tam_target_size, sdrs_count, ae_count, linkedin_count, clickup_backlog_url, tam_list_url, blocklist_url, tam_accounts_url, tam_contacts_url, slack_main_channel, slack_reporting_channel, products, geos, notes, domains_included, mailboxes_included, cold_calling_included, reply_handling, product_categories, product_description, icp_titles, icp_departments, compatibility_requirements, suitability_requirements, crm_system, crm_credentials, sequencer_system, sequencer_credentials, enrichment_tools, enrichment_credentials, slack_sales_channel, slack_positive_replies_channel, slack_neutral_negative_replies_channel, secondary_contact_email, billing_name, billing_email, meeting_links, sales_team_emails, sales_team_names, sales_team_roles, sales_team_regions, sales_team_calendly_links, tam_segments, tam_uploaded_data')
          .eq('status', 'Active')
          .neq('id', 'default')
          .not('name', 'ilike', '%global%default%keys%')
          .not('name', 'ilike', '%default%keys%')
          .not('name', 'ilike', '%outboundbuddy%')
          .order('name', { ascending: true })

        if (error) throw error
        
        // If we have real data, use it
        if (data && data.length > 0) {
          return data as Client[]
        }
        
        // Only fall back to demo data if Supabase returns empty AND Smartlead API is not configured
        if (!smartleadAPI.isConfigured()) {
          console.log('🔑 [useClients] No clients in database and Smartlead API not configured, returning demo clients...')
          const domainCounts = getMailboxCountByDomain()
          const mappedDemoClients: Client[] = DEMO_CLIENTS.map(dc => ({
            id: dc.id.toString(),
            name: dc.name,
            created_at: dc.created_at,
            updated_at: dc.updated_at,
            status: 'Active',
            // Add realistic contact information
            email: dc.contact_email || `${dc.name.toLowerCase().replace(/\s+/g, '')}@company.com`,
            phone: dc.contact_phone || '+1 (555) 123-4567',
            address: `${dc.name} Headquarters, 123 Business St, Suite 100, San Francisco, CA 94105`,
            industry: dc.industry || '',
            company: dc.name,
            notes: `Primary contact: ${dc.contact_name || 'Contact Person'}`,
            // Add client_id and contact fields for the Clients List
            client_id: dc.client_id || dc.id,
            main_contact_email: dc.contact_email || `${dc.name.toLowerCase().replace(/\s+/g, '')}@company.com`,
            main_account_manager: dc.contact_name || 'Contact Person',
            domain: null,
            // Derive mailbox counts from domain plan
            total_mailboxes: DEMO_DOMAINS
              .filter(d => d.client_id === dc.id)
              .reduce((sum, d) => sum + (domainCounts[d.domain] || 0), 0)
          } as Client))
          console.log('📊 Demo: Loaded', mappedDemoClients.length, 'demo clients')
          return mappedDemoClients
        }
        
        // Return empty array if no data and Smartlead is configured (just no clients yet)
        return []
      } catch (error) {
        console.error('Error fetching clients from Supabase:', error)
        // Only use demo data as fallback if Smartlead API is not configured
        if (!smartleadAPI.isConfigured()) {
          console.log('🔑 [useClients] Database error and Smartlead API not configured, returning demo clients...')
          const domainCounts = getMailboxCountByDomain()
          const mappedDemoClients: Client[] = DEMO_CLIENTS.map(dc => ({
            id: dc.id.toString(),
            name: dc.name,
            created_at: dc.created_at,
            updated_at: dc.updated_at,
            status: 'Active',
            email: dc.contact_email || `${dc.name.toLowerCase().replace(/\s+/g, '')}@company.com`,
            phone: dc.contact_phone || '+1 (555) 123-4567',
            address: `${dc.name} Headquarters, 123 Business St, Suite 100, San Francisco, CA 94105`,
            industry: dc.industry || '',
            company: dc.name,
            notes: `Primary contact: ${dc.contact_name || 'Contact Person'}`,
            client_id: dc.client_id || dc.id,
            main_contact_email: dc.contact_email || `${dc.name.toLowerCase().replace(/\s+/g, '')}@company.com`,
            main_account_manager: dc.contact_name || 'Contact Person',
            domain: null,
            total_mailboxes: DEMO_DOMAINS
              .filter(d => d.client_id === dc.id)
              .reduce((sum, d) => sum + (domainCounts[d.domain] || 0), 0)
          } as Client))
          return mappedDemoClients
        }
        throw error
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useClientsList() {
  return useQuery({
    queryKey: ['clients-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, status, client_id')
        .eq('status', 'Active')
        .neq('id', 'default')
        .not('name', 'ilike', '%global%default%keys%')
        .not('name', 'ilike', '%default%keys%')
        .not('name', 'ilike', '%outboundbuddy%')
        .order('name', { ascending: true })

      if (error) {
        console.error('Error fetching clients list from Supabase:', error)
        throw error
      }

      return (data || []) as ClientBasic[]
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}